// @flow
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { reduce, map, filter, pipe, append, zip, path } from 'ramda';
import { useTranslation } from 'react-i18next';

import type {
  ComplexMap,
  Complex,
} from 'app/common/components/Cafeteria/dataTypes/order/Complex.types';
import type { OrderSettings } from 'app/common/components/Cafeteria/dataTypes/order/OrderSettings.types';
import type {
  OrderItemMap,
  OrderItem,
} from 'app/common/components/Cafeteria/dataTypes/order/OrderItem.types';
import createOrderItem from 'app/common/components/Cafeteria/dataTypes/order/createOrderItem';

import type { Available } from '../../../../dataTypes/orderRequests/Available.types';
import {
  groupTypes,
  type GroupType,
} from '../../../../dataTypes/orderRequests/groupTypes';
import getIsSourceItemDisabled from './getIsSourceItemDisabled';


type State = {
  sourceItems: Array<Complex>,
  orderItemsMap: OrderItemMap,
  orderItems: Array<OrderItem>,
  orderSettings: OrderSettings,
  targetId: string | null,
  targetName?: string,
  sourceId: string | null,
  changes: Array<{
    id: string,
    from: string,
    to: string,
  }>,
};

type Actions = {
  setComplexes: ComplexMap => void,
  initToOrder: (id: string, amount?: number) => void,
  resetOrder: () => void,
  setTargetId: (id: string) => void,
  resetTargetId: () => void,
  setSourceId: (id: string) => void,
  addExchange: () => void,
};

const deaultOrderState: OrderItemMap = new Map([]);
const deaultComplexesState: ComplexMap = new Map([]);

export default function useExchangeOrder(
  available: Available,
  groupType: GroupType | null,
): [State, Actions] {
  const { t } = useTranslation('cafeteria');

  const lockControls = useRef(false);

  const [complexes, setComplexes] = useState<ComplexMap>(deaultComplexesState);
  const [orderItems, setOrderItems] = useState<OrderItemMap>(deaultOrderState);
  const [targetId, setTargetId] = useState<string | null>(null);
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [initialOrderItemsIds, setInitialOrderItemsIds] = useState<Array<string>>([]);

  const resetTargetId = useCallback(() => {
    setTargetId(null);
    setSourceId(null);
  }, []);

  const addExchange = useCallback(() => {
    if (lockControls.current) return;
    if (targetId && sourceId) {
      const sourceComplexItem = complexes.get(sourceId);
      if (sourceComplexItem) {
        lockControls.current = true;
        setOrderItems(
          state =>
            new Map(
              map((orderPair) => {
                const [id, order] = orderPair;
                const { cost } = sourceComplexItem;
                if (id === targetId && cost) {
                  const isNew = !initialOrderItemsIds.includes(sourceId);

                  return [
                    sourceId,
                    createOrderItem({
                      id: sourceComplexItem.id,
                      title: sourceComplexItem.title,
                      cost,
                      freeCount: sourceComplexItem.freeCount,
                      amount: order.amount,
                      complex: sourceComplexItem,
                      isNew,
                    }),
                  ];
                }
                return orderPair;
              }, Array.from(state.entries())),
            ),
        );
        resetTargetId();
      }
    }
  }, [targetId, sourceId, complexes, initialOrderItemsIds, resetTargetId]);

  const orderSettings = useMemo(() => {
    const { dayCareOrderAvailable, klassOrderAvailable } = available;
    const balanceAvailable
      = groupType === groupTypes.DayCare
        ? dayCareOrderAvailable
        : klassOrderAvailable;
    const totalInOrder = reduce(
      (acc, { cost, amount, freeCount }) =>
        acc + cost * amount - cost * freeCount,
      0,
      Array.from(orderItems.values()),
    );

    return {
      totalInOrder,
      balanceAvailable: Math.max((balanceAvailable || 0) - totalInOrder, 0),
    };
  }, [orderItems, available, groupType]);

  const { balanceAvailable } = orderSettings;

  const initToOrder = useCallback(
    (itemId: string, amount?: number = 1) => {
      const targetComplex = complexes.get(itemId);
      if (targetComplex) {
        const { id, title, cost, freeCount } = targetComplex;
        if (cost !== null) {
          setInitialOrderItemsIds(append(id));
          setOrderItems(
            state =>
              new Map([
                ...Array.from(state.entries()),
                [
                  itemId,
                  createOrderItem({
                    id,
                    title,
                    cost,
                    freeCount,
                    amount,
                    complex: targetComplex,
                  }),
                ],
              ]),
          );
        }
      }
    },
    [complexes],
  );

  const resetOrder = useCallback(() => {
    setOrderItems(deaultOrderState);
    setTargetId(null);
  }, []);

  const sourceItems = useMemo(() => {
    if (targetId) {
      return pipe(
        filter(({ id }) => !orderItems.get(id)),
        map((props) => {
          const { cost, freeCount } = props;
          const targetOrderItem = orderItems.get(targetId);
          const {
            amount,
            cost: targetItemCost,
            freeCount: targetItemFreeCount,
          } = targetOrderItem || { amount: 0, cost: null, freeCount: 0 };

          const disabledReasonTranslateKey = getIsSourceItemDisabled({
            balanceAvailable,
            amount,
            targetItemCost,
            souceItemCost: cost,
            targetItemFreeCount,
            sourceItemFreeCount: freeCount,
          });
          const disabled = !!disabledReasonTranslateKey;

          return {
            ...props,
            disabled,
            disabledReason: t(disabledReasonTranslateKey),
          };
        }),
      )(Array.from(complexes.values()));
    }
    return [];
  }, [complexes, targetId, orderItems, balanceAvailable, t]);

  const targetComplex = (targetId && complexes.get(targetId)) || null;
  const targetName = (targetComplex && targetComplex.title) || undefined;

  const orderItemsArray = useMemo(
    () => Array.from(orderItems.values()),
    [orderItems],
  );

  useEffect(() => {
    lockControls.current = false;
  }, [orderItemsArray]);

  const changes = useMemo(
    () =>
      pipe(
        zip(initialOrderItemsIds),
        filter(path([1, 'isNew'])),
        map(([id, { title }]) => ({
          id,
          from: (complexes.get(id) || { title: '???' }).title,
          to: title,
        })),
      )(orderItemsArray),
    [orderItemsArray, initialOrderItemsIds, complexes],
  );

  return [
    {
      orderItemsMap: orderItems,
      orderItems: orderItemsArray,
      sourceItems,
      orderSettings,
      targetId,
      targetName,
      sourceId,
      changes,
    },
    {
      setComplexes,
      initToOrder,
      resetOrder,
      setTargetId,
      resetTargetId,
      setSourceId,
      addExchange,
    },
  ];
}
